import type { SortByField, SortOrder } from '../../services/api/flightChanges';

export const DEFAULT_FLIGHT_CHANGE_CASES_SORT_BY: SortByField = 'loadDate';
export const DEFAULT_FLIGHT_CHANGE_CASES_SORT_ORDER: SortOrder = 'desc';
export const DEFAULT_FLIGHT_CHANGE_CASES_PAGE = 0;
export const DEFAULT_FLIGHT_CHANGE_CASES_COUNT = 10;

const SORT_BY_FIELDS = new Set<SortByField>([
    'awb',
    'loadDate',
    'route',
    'movementNumber',
    'analyst',
    'status',
    'notificationStatus',
]);

/** Same codes as sent to GET .../flight-change-cases (`status` query param, CSV). */
export interface FlightChangeCasesListSearchState {
    sortBy: SortByField;
    sortOrder: SortOrder;
    page: number;
    count: number;
    statusCodes: string[];
    /** Raw AWB filter text (short values persist in URL; API sends awb only when length rules allow). */
    awbInput: string;
    analystId: number | null;
    /** When true, list only cases without an assigned analyst (API: isUnassigned=true). */
    filterUnassigned: boolean;
}

export function isDefaultSort(sortBy: SortByField, sortOrder: SortOrder): boolean {
    return sortBy === DEFAULT_FLIGHT_CHANGE_CASES_SORT_BY && sortOrder === DEFAULT_FLIGHT_CHANGE_CASES_SORT_ORDER;
}

function isValidSortBy(value: string | null): value is SortByField {
    return value != null && SORT_BY_FIELDS.has(value as SortByField);
}

function normalizeSearchString(search: string): string {
    if (search === '' || search === '?') {
        return '';
    }
    return search.startsWith('?') ? search.slice(1) : search;
}

function parsePage(raw: string | null): number {
    if (raw == null || raw === '') {
        return DEFAULT_FLIGHT_CHANGE_CASES_PAGE;
    }
    const n = Number.parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) {
        return DEFAULT_FLIGHT_CHANGE_CASES_PAGE;
    }
    return n;
}

function parsePositiveInt(raw: string | null, fallback: number): number {
    if (raw == null || raw === '') {
        return fallback;
    }
    const n = Number.parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) {
        return fallback;
    }
    return n;
}

function parseStatusCodes(raw: string | null): string[] {
    if (raw == null || raw === '') {
        return [];
    }
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

function parseAnalystId(raw: string | null): number | null {
    if (raw == null || raw === '') {
        return null;
    }
    const n = Number.parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
}

function parseFilterUnassigned(params: URLSearchParams): boolean {
    return params.get('isUnassigned') === 'true';
}

/**
 * Reads list query state from a URL search string (with or without leading "?").
 * Aligns with GET /flight/api/v1/flight-change-cases query params (sort, page, count, status, awb, analystId).
 */
export function parseFlightChangeCasesSearch(search: string): FlightChangeCasesListSearchState {
    const normalized = normalizeSearchString(search);
    const params = new URLSearchParams(normalized);

    const sortByRaw = params.get('sortBy');
    const sortBy = isValidSortBy(sortByRaw) ? sortByRaw : DEFAULT_FLIGHT_CHANGE_CASES_SORT_BY;

    const sortOrderRaw = params.get('sortOrder');
    const sortOrder: SortOrder =
        sortOrderRaw === 'asc' || sortOrderRaw === 'desc'
            ? sortOrderRaw
            : DEFAULT_FLIGHT_CHANGE_CASES_SORT_ORDER;

    const page = parsePage(params.get('page'));
    const count = parsePositiveInt(params.get('count'), DEFAULT_FLIGHT_CHANGE_CASES_COUNT);

    const statusCodes = parseStatusCodes(params.get('status'));
    const awbInput = params.get('awb') ?? '';
    const filterUnassigned = parseFilterUnassigned(params);
    // Mutual exclusion: isUnassigned wins; analystId in the URL is dropped when parsing.
    const analystId = filterUnassigned ? null : parseAnalystId(params.get('analystId'));

    return { sortBy, sortOrder, page, count, statusCodes, awbInput, analystId, filterUnassigned };
}

/**
 * Builds a search string (no leading "?") for the list route.
 * Omits defaults: sort loadDate+desc, page 0, count 10, empty filters.
 */
export function serializeFlightChangeCasesSearch(state: FlightChangeCasesListSearchState): string {
    const params = new URLSearchParams();

    if (state.page !== DEFAULT_FLIGHT_CHANGE_CASES_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.count !== DEFAULT_FLIGHT_CHANGE_CASES_COUNT) {
        params.set('count', String(state.count));
    }
    if (!isDefaultSort(state.sortBy, state.sortOrder)) {
        params.set('sortBy', state.sortBy);
        params.set('sortOrder', state.sortOrder);
    }
    if (state.statusCodes.length > 0) {
        params.set('status', state.statusCodes.join(','));
    }
    const awbTrimmed = state.awbInput.trim();
    if (awbTrimmed !== '') {
        params.set('awb', awbTrimmed);
    }
    // Mutual exclusion: only one analyst filter param is written (never both).
    if (state.filterUnassigned) {
        params.set('isUnassigned', 'true');
    } else if (state.analystId != null) {
        params.set('analystId', String(state.analystId));
    }

    return params.toString();
}

/** Minimal navigate signature (react-router-dom) for list search updates without importing RR here. */
export type FlightChangeCasesNavigate = (
    to: { pathname: string; search: string },
    options?: { replace?: boolean },
) => void;

/**
 * Updates the browser URL for the flight-change-cases list query, keeping pathname stable.
 * Use `replace: true` for high-frequency updates (e.g. AWB typing) so "back" restores sort/page, not each character.
 */
export function navigateFlightChangeCasesSearch(
    navigate: FlightChangeCasesNavigate,
    pathname: string,
    currentSearch: string,
    state: FlightChangeCasesListSearchState,
    options?: { replace?: boolean },
): void {
    const search = serializeFlightChangeCasesSearch(state);
    const nextSearch = search ? `?${search}` : '';
    if (nextSearch === currentSearch) {
        return;
    }
    navigate({ pathname, search: nextSearch }, { replace: options?.replace ?? false });
}
